import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Order } from '@app/core/business/order/order';
import { OrderStatus } from '@app/core/business/order/order-status';
import { HistoryListProperty } from '@app/core/model/historyListPropertyInput';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@env/environment';
import { OrderErrorLine } from '@app/core/business/order/order-error-line';
import { ContextService } from '@app/core/service/context.service';
import { Weight } from '@app/core/business/weight';
import { Price } from '@app/core/business/price';
import { Offer } from '@app/core/business/offer/offer';
import { FrancoComponent } from '@app/core/ui/franco/franco.component';
import { Franco } from '@app/core/business/franco/franco';
import { Console } from 'console';
import { untilDestroyed } from 'ngx-take-until-destroy';

@Component({
  selector: 'app-order-summary',
  templateUrl: './order-summary.component.html',
  styleUrls: ['./order-summary.component.scss'],
})
export class OrderSummaryComponent implements OnInit, OnDestroy {
  @Input()
  public order: Order;
  @Input()
  public validatedTerms: boolean;
  @Input()
  public isMobile: boolean;
  public contextOrder: Order;


  @Output()
  public validateOrderEmitter: EventEmitter<any>;
  @Output()
  public cancelOrderEmitter: EventEmitter<any>;
  @Output()
  public reactivateOrderEmitter: EventEmitter<any>;
  @Output()
  public continueOrderEmitter: EventEmitter<any>;

  @ViewChild('headerTemplate', { static: true })
  private headerTemplate: TemplateRef<any>;
  @ViewChild('priceTemplate', { static: true })
  private priceTemplate: TemplateRef<any>;
  @ViewChild('priceUnitTemplate', { static: true })
  private priceUnitTemplate: TemplateRef<any>;
  @ViewChild('priceTotalTemplate', { static: true })
  private priceTotalTemplate: TemplateRef<any>;
  @ViewChild('labelTemplate', { static: true })
  private labelTemplate: TemplateRef<any>;
  @ViewChild('eanTemplate', { static: true })
  private eanTemplate: TemplateRef<any>;
  @ViewChild('dateCourteTemplate', { static: true })
  private dateCourteTemplate: TemplateRef<any>;
  @ViewChild('brandTemplate', { static: true })
  private brandTemplate: TemplateRef<any>;
  @ViewChild('priceByWeightTemplate', { static: true })
  private priceByWeightTemplate: TemplateRef<any>;
  @ViewChild('weightTemplate', { static: true })
  private weightTemplate: TemplateRef<any>;
  @ViewChild('quantityTemplate', { static: true })
  private quantityTemplate: TemplateRef<any>;
  @ViewChild('productTemplate', { static: true })
  private productTemplate: TemplateRef<any>;
  @ViewChild('discountTemplate', { static: true })
  private discountTemplate: TemplateRef<any>;

  // Affichage de la première image associée au produit
  @ViewChild('productImageTemplate', { static: true })
  private productImageTemplate: TemplateRef<any>;

  public orderStatus = OrderStatus;
  public orderTypeLabel: string;
  public columns: any;
  public outOfStockColumns: any;
  public orderLinesColumns: any;
  public limit = 20;
  public properties: HistoryListProperty[];
  public environment = environment;
  public errors: OrderErrorLine[] = JSON.parse(
    localStorage.getItem('mergeErrorLines')
  );
  public showOrderSummaryAdditionnalInfos = false;

  constructor(
    private translateService: TranslateService,
    public contextService: ContextService
  ) {
    this.cancelOrderEmitter = new EventEmitter<any>();
    this.validateOrderEmitter = new EventEmitter<any>();
    this.reactivateOrderEmitter = new EventEmitter<any>();
    this.continueOrderEmitter = new EventEmitter<any>();
  }

  ngOnInit() {


    this.contextService.fullContext$
      .pipe(untilDestroyed(this))
      .subscribe(async fullContext => {
        this.initOutOfStockColumns();
        this.initOrderLinesColumns();
        this.initMobileListProperties();

        this.initOrderTypeLabel();
        this.contextOrder = fullContext?.order;
      });
    //console.log('Order summary - order : ', this.order);
  }

  //Datecourte, renvoie la classe CSS correspondante si ligne est date courte
  public getRowClass = (row: any) => {
    return {
      'date-courte-row': !!row?.isDateCourte
    };
  };

  /**
   * CLA le 10/10/2019 - A/B - Permet de définir le libellé du type de la commande
   */
  private initOrderTypeLabel() {
    this.contextService.fullContext.customer?.orderTypes?.forEach(orderType => {
      if (orderType.code === this.order?.orderType) {
        this.orderTypeLabel = orderType.label;
      }
    });
  }

  ngOnDestroy(): void {
    // Nothing to do
  }
  private sortDiscounts(a, b) {
    return this.sort(a[0], b[0]);
  }

  private initOutOfStockColumns(): void {
    this.outOfStockColumns = [
      {
        name: this.translateService.instant('product.pictures'),
        prop: 'pictures',
        cellTemplate: this.productImageTemplate,
        flexGrow: 1.75,
        resizeable: false,
      },
      {
        name: this.translateService.instant('product.code'),
        prop: 'productCode',
        cellTemplate: this.productTemplate,
        flexGrow: 1,
        resizeable: false,
      },
      {
        name: this.translateService.instant('product.brand'),
        prop: 'brand',
        cellTemplate: this.brandTemplate,
        flexGrow: 1,
        resizeable: false,
      },
      {
        name: this.translateService.instant('product.label'),
        prop: 'label',
        cellTemplate: this.labelTemplate,
        flexGrow: 1,
        resizeable: false,
      },
      {
        name: this.translateService.instant('product.ean'),
        prop: 'ean',
        cellTemplate: this.productTemplate,
        flexGrow: 1,
        resizeable: false,
        transform: (value: any): string => {
          return '"' + value + '"';
        }
      }];

    if (this.contextService.fullContext?.customer?.dateCourte) {
      this.outOfStockColumns.push(
        {
          name: 'Anti Gaspi',
          prop: 'isDateCourte',
          cellTemplate: this.dateCourteTemplate,
          flexGrow: 0.7,
          resizeable: false,
          comparator: (a: boolean, b: boolean) => (a === b ? 0 : a ? 1 : -1),
          transform: (v: boolean) => (v ? 'true' : 'false')
        },);
    }

    this.outOfStockColumns.push(
      {
        name: this.translateService.instant('order.quantity'),
        prop: 'quantity',
        flexGrow: 1,
        resizeable: false,
      },
      {
        name: this.translateService.instant('product.weight'),
        prop: 'weight.gross',
        cellTemplate: this.weightTemplate,
        comparator: this.sortFloat.bind(this),
        flexGrow: 1,
        resizeable: false,
        transform: (weight: Weight): string => {
          var result = "";
          if (weight.gross)
            result += weight.gross + weight.unit + " " + this.translateService.instant('product.weights.grossShort');
          if (weight.net)
            result += " " + weight.net + weight.unit + " " + this.translateService.instant('product.weights.netShort');

          return result;
        }
      },
      {
        name: this.translateService.instant('product.prices.byWeight'),
        prop: 'price.grossByWeight',
        cellTemplate: this.priceByWeightTemplate,
        comparator: this.sortFloat.bind(this),
        flexGrow: 1,
        resizeable: false,
        transform: (price: Price): string => {
          var result = "";
          if (price.grossByWeight)
            result += price.grossByWeight + price.unit + '/' + price.weightUnit + " " + this.translateService.instant('product.prices.grossShort');
          if (price.netByWeight)
            result += " " + price.netByWeight + price.unit + '/' + price.weightUnit + " " + this.translateService.instant('product.prices.netShort');;

          return result;
        }
      },
      /**
      {
        name: this.translateService.instant('product.prices.discount'),
        prop: 'price.discount',
        cellTemplate: this.priceTemplate,
        comparator: this.sortFloat.bind(this),
        flexGrow: 1,
        resizeable: false,
      }, */
      {
        name: this.translateService.instant('product.prices.discount'),
        prop: 'price.discounts',
        cellTemplate: this.discountTemplate,
        comparator: this.sortDiscounts.bind(this),
        flexGrow: 1.3,
        resizeable: false,
        transform: (discounts: string[], row: any) =>
          row.price.discounts.join(', '),
      },
      {
        name: this.translateService.instant('product.outer'),
        prop: 'outer',
        flexGrow: 0.5,
        resizeable: false,
      },
      {
        name: this.translateService.instant('product.price'),
        prop: 'price.gross',
        cellTemplate: this.priceUnitTemplate,
        comparator: this.sortFloat.bind(this),
        flexGrow: 1,
        resizeable: false,
        transform: (price: Price): string => {
          var result = "";
          if (price.gross)
            result += price.gross + price.unit + " " + this.translateService.instant('product.prices.grossShort');
          if (price.net)
            result += " " + price.net + price.unit + " " + this.translateService.instant('product.prices.grossShort');

          return result;
        }
      },
      {
        name: this.translateService.instant('product.price'),
        prop: 'price.total',
        cellTemplate: this.priceTotalTemplate,
        comparator: this.sortFloat.bind(this),
        flexGrow: 1,
        resizeable: false,
        transform: (price: Price): string => {
          var result = "";
          if (price.gross)
            result += price.gross + price.unit + " " + this.translateService.instant('product.prices.grossShort');
          if (price.net)
            result += " " + price.net + price.unit + " " + this.translateService.instant('product.prices.grossShort');

          return result;
        }
      });
  }

  private initOrderLinesColumns(): void {
    this.orderLinesColumns = [
      {
        name: this.translateService.instant('product.pictures'),
        prop: 'pictures',
        cellTemplate: this.productImageTemplate,
        flexGrow: 1,
        resizeable: false,
      },
      {
        name: this.translateService.instant('product.code'),
        prop: 'productCode',
        cellTemplate: this.productTemplate,
        flexGrow: 1,
        resizeable: false,
      },
      {
        name: this.translateService.instant('product.brand'),
        prop: 'brand',
        cellTemplate: this.brandTemplate,
        flexGrow: 1,
        resizeable: false,
      },
      {
        name: this.translateService.instant('product.label'),
        prop: 'label',
        cellTemplate: this.labelTemplate,
        flexGrow: 2,
        resizeable: false,
      },
      {
        name: this.translateService.instant('product.ean'),
        prop: 'ean',
        cellTemplate: this.productTemplate,
        flexGrow: 1,
        resizeable: false,
        transform: (value: any): string => {
          return '"' + value + '"';
        }
      }];
    if (this.contextService.fullContext?.customer?.dateCourte) {
      this.orderLinesColumns.push(
        {
          name: 'Anti Gaspi',
          prop: 'isDateCourte',
          cellTemplate: this.dateCourteTemplate,
          flexGrow: 0.7,
          resizeable: false,
          comparator: (a: boolean, b: boolean) => (a === b ? 0 : a ? 1 : -1),
          transform: (v: boolean) => (v ? 'true' : 'false')
        });
    }
    this.orderLinesColumns.push(
      {
        name: this.translateService.instant('product.weight'),
        prop: 'weight.gross',
        cellTemplate: this.weightTemplate,
        comparator: this.sortFloat.bind(this),
        flexGrow: 1,
        resizeable: false,
        transform: (weight: Weight): string => {
          var result = "";
          if (weight.gross)
            result += weight.gross + weight.unit + " " + this.translateService.instant('product.weights.grossShort');
          if (weight.net)
            result += " " + weight.net + weight.unit + " " + this.translateService.instant('product.weights.netShort');

          return result;
        }
      },
      {
        name: this.translateService.instant('product.prices.byWeight'),
        prop: 'price.grossByWeight',
        cellTemplate: this.priceByWeightTemplate,
        comparator: this.sortFloat.bind(this),
        flexGrow: 1,
        resizeable: false,
        transform: (price: Price): string => {
          var result = "";
          if (price.grossByWeight)
            result += price.grossByWeight + price.unit + '/' + price.weightUnit + " " + this.translateService.instant('product.prices.grossShort');
          if (price.netByWeight)
            result += " " + price.netByWeight + price.unit + '/' + price.weightUnit + " " + this.translateService.instant('product.prices.netShort');;

          return result;
        }
      },
      /** {
        name: this.translateService.instant('product.prices.discount'),
        prop: 'price.discount',
        cellTemplate: this.priceTemplate,
        comparator: this.sortFloat.bind(this),
        flexGrow: 1,
        resizeable: false,
      },*/
      {
        name: this.translateService.instant('product.prices.discount'),
        prop: 'price.discounts',
        cellTemplate: this.discountTemplate,
        comparator: this.sortDiscounts.bind(this),
        flexGrow: 1.3,
        resizeable: false,
        transform: (discounts: string[], row: any) =>
          row.price.discounts.join(', '),
      },
      {
        name: this.translateService.instant('product.outer'),
        prop: 'outer',
        flexGrow: 1,
        resizeable: false,
      },
      {
        name: this.translateService.instant('product.price'),
        prop: 'price.gross',
        cellTemplate: this.priceUnitTemplate,
        comparator: this.sortFloat.bind(this),
        flexGrow: 1,
        resizeable: false,
        transform: (price: Price): string => {
          var result = "";
          if (price.gross)
            result += price.gross + price.unit + " " + this.translateService.instant('product.prices.grossShort');
          if (price.net)
            result += " " + price.net + price.unit + " " + this.translateService.instant('product.prices.grossShort');

          return result;
        }
      },
      {
        name: this.translateService.instant('product.prices.total'),
        prop: 'price.total',
        cellTemplate: this.priceTotalTemplate,
        comparator: this.sortFloat.bind(this),
        flexGrow: 1,
        resizeable: false,
        transform: (price: Price): string => {
          var result = "";
          if (price.total)
            result += price.total + price.unit + " " + this.translateService.instant('product.prices.exclVAT');
          if (price.totalAfterTaxes)
            result += " " + price.totalAfterTaxes + price.unit + " " + this.translateService.instant('product.prices.inclVAT');

          return result;
        }
      },
      {
        name: this.translateService.instant('product.quantity'),
        cellTemplate: this.quantityTemplate,
        flexGrow: 2,
        resizeable: false,
        sortable: false,
      });

  }

  public cancelOrder(): void {
    this.cancelOrderEmitter.emit();
  }

  public validateOrder(): void {
    this.validateOrderEmitter.emit();
  }

  public continueOrder(): void {
    this.continueOrderEmitter.emit();
  }

  public reactivateOrder(): void {
    this.reactivateOrderEmitter.emit();
  }

  private sortFloat(a: string, b: string): number {
    return this.sort(
      parseFloat(a.replace(',', '.')),
      parseFloat(b.replace(',', '.'))
    );
  }

  private sort(a: any, b: any): number {
    if (a > b) {
      return 1;
    }
    if (a < b) {
      return -1;
    }
    return 0;
  }

  public setCompany(companyCode: string) {
    this.contextService.setSelectedCompany(companyCode);
  }

  private initMobileListProperties(): void {
    this.properties = new Array<HistoryListProperty>();
    this.properties.push({
      label: 'product.code',
      key: 'productCode',
      customTemplate: this.productTemplate,
    });
    this.properties.push({
      label: 'product.ean',
      key: 'ean',
      customTemplate: this.eanTemplate,
    });
    this.properties.push({
      label: 'product.brand',
      key: 'brand',
      customTemplate: this.brandTemplate,
    });
    this.properties.push({
      label: 'product.label',
      key: 'label',
      customTemplate: this.labelTemplate,
    });
    this.properties.push({
      label: 'product.weight',
      key: 'weight.gross',
      customTemplate: this.weightTemplate,
    });
    this.properties.push({
      label: 'product.prices.byWeight',
      key: 'price.grossByWeight',
      customTemplate: this.priceByWeightTemplate,
    });
    this.properties.push({
      label: 'product.prices.discount',
      key: 'price.discount',
      customTemplate: this.priceTemplate,
    });
    this.properties.push({
      label: 'product.prices.discount',
      key: 'price.discounts',
      customTemplate: this.discountTemplate,
    });
    this.properties.push({ label: 'product.outer', key: 'outer' });
    this.properties.push({
      label: 'product.price',
      key: 'price.total',
      customTemplate: this.priceTotalTemplate,
    });
  }

  public isFrancoAtteint(): boolean {
    var price = parseFloat(this.order.price.total.replace(/\s/g, '').replace(',', '.'));
    return price >= this.order.price.seuilFranco;
  }



  public toggleInfos() {
    this.showOrderSummaryAdditionnalInfos = !this.showOrderSummaryAdditionnalInfos;
  }
}
